/**
 * OAuth callback handler
 * Processes tokens from OAuth redirect and logs user in
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AuthCallback.css';

/**
 * Parse key=value pairs from a URL fragment (hash).
 * Tokens are passed in the fragment (#) to prevent leakage in server logs,
 * browser history, and referrer headers.
 */
function parseFragment(): Record<string, string> {
  const hash = window.location.hash.substring(1); // remove leading #
  const params: Record<string, string> = {};
  for (const part of hash.split('&')) {
    const [key, value] = part.split('=');
    if (key && value) params[key] = decodeURIComponent(value);
  }
  return params;
}

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      // Get tokens from URL fragment (not query params, for security)
      const fragment = parseFragment();
      const accessToken = fragment['access_token'];
      const refreshToken = fragment['refresh_token'];
      const errorParam = fragment['error'];

      if (errorParam) {
        setError(errorParam);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      if (!accessToken || !refreshToken) {
        setError('Missing authentication tokens');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      try {
        // Log in with tokens
        await login(accessToken, refreshToken);

        // Redirect to dashboard
        navigate('/');
      } catch (err: any) {
        console.error('Login failed:', err);
        setError('Failed to complete login. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    processCallback();
    // Clear the fragment from the URL to prevent token exposure in browser history
    window.history.replaceState(null, '', window.location.pathname);
  }, [login, navigate]);

  return (
    <div className="auth-callback-container">
      <div className="auth-callback-card">
        {error ? (
          <>
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
              </svg>
            </div>
            <h2>Authentication Failed</h2>
            <p>{error}</p>
            <p className="redirect-message">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="spinner"></div>
            <h2>Completing Sign In</h2>
            <p>Please wait while we log you in...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
